import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers, XCircle, ShieldAlert } from "lucide-react";
import { useSEO } from "../utils/seo";
import { CopyDetails } from "../components/CopyDetails";

const AreaScore = () => {
  useSEO({
    title: "AreaScore | Site Evaluation | Onward Land Solutions",
    description:
      "Evaluate a property or search area with AreaScore. Bring ownership, access, infrastructure and permitting considerations into a scoped site evaluation before further due diligence.",
    canonicalPath: "/area-score",
  });

  const inputs = [
    "One candidate property or project area, a county or broader search region, or project criteria",
    "Project purpose, facility type, and intended use",
    "Land size or acreage target",
    "Known access, infrastructure, setback, and siting requirements",
    "Must-have and must-avoid conditions",
    "Available GIS, engineering, utility, or client data, if available",
    "Schedule, decision milestone, priorities, and deliverable requirements",
  ];

  const reviewAreas = [
    "Parcel configuration and acreage",
    "Ownership and title leads",
    "Access, terrain and working constraints",
    "Nearby gas, power, water, fiber and transport (proximity, not confirmed capacity or availability)",
    "Environmental and permitting follow-up",
    "Appraisal-district records and land-cost context, not formal valuation",
    "Source dates, availability and gaps",
  ];

  const deliverables = [
    "Comparison map for the agreed site/area scope",
    "Findings and tradeoffs, with source gaps",
    "Agreed GIS files",
    "Prioritized due-diligence plan",
    "Review discussion with your team",
  ];

  const notWhat = [
    "Automatic or autonomous site selection",
    "Buildability, availability, or construction-readiness determination",
    "Utility or interconnection capacity or queue position",
    "Engineering feasibility approval",
    "Environmental determination",
    "Permitting or agency approval",
    "Legal or title opinion",
    "Licensed survey certification",
    "A fully autonomous software platform",
  ];

  return (
    <>
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #090A0C 0%, #101720 55%, #090A0C 100%)" }} data-testid="areascore-hero">
        <div className="ols-parcel-grid absolute inset-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <p className="ols-eyebrow ols-eyebrow--light mb-6" style={{ color: "#C8102E" }} data-testid="areascore-eyebrow">
            AreaScore | Site Evaluation
          </p>
          <div className="flex items-center gap-4 mb-4">
            <Layers className="h-10 w-10 shrink-0" style={{ color: "#E31837" }} />
            <h1 className="ols-h1" data-testid="areascore-h1">Find the site worth pursuing.</h1>
          </div>
          <p className="text-lg text-slate-200 max-w-3xl mt-6 leading-relaxed" data-testid="areascore-intro">
            Evaluate a property or search area before committing more time and capital. AreaScore brings ownership, access, nearby infrastructure and permitting considerations into one land review.
          </p>
          <p className="italic text-[15px] mt-4" style={{ color: "#F5F2EC" }} data-testid="areascore-proximity-boundary">
            Proximity does not establish capacity or availability. Appropriate specialists confirm technical conclusions.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link to="/contact?type=AreaScore&source=areascore_demo" className="ols-btn-primary" data-testid="areascore-cta-primary">
              Talk to Onward About AreaScore <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
            <Link to="/path-score?source=areascore_connect_points" className="text-sm text-white underline underline-offset-4" data-testid="areascore-pathscore-link">Need to connect two points? Explore PathScore.</Link>
          </div>
        </div>
      </section>

      {/* WHO + WHEN */}
      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
          <div>
            <p className="ols-eyebrow mb-3">Who AreaScore Is For</p>
            <h2 className="ols-h3 mb-4" style={{ color: "var(--ols-navy)" }}>Land-intensive development.</h2>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
              Renewable, industrial and energy teams comparing candidate areas.
            </p>
          </div>
          <div>
            <p className="ols-eyebrow mb-3">When to Use It</p>
            <h2 className="ols-h3 mb-4" style={{ color: "var(--ols-navy)" }}>Before deeper due diligence.</h2>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
              Identify land questions before deeper permitting, engineering and acquisition work.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU PROVIDE */}
      <section className="ols-section" style={{ background: "var(--ols-limestone)" }} data-testid="areascore-inputs-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="ols-eyebrow mb-3">What You Provide</p>
          <h2 className="ols-h2 mb-4" style={{ color: "var(--ols-navy)" }}>Start with what you know.</h2>
          <p className="text-[15px] leading-relaxed mb-8" style={{ color: "var(--ols-body)", maxWidth: "68ch" }}>
            Share the location, intended use and decision question. Multiple candidate areas are welcome, not required.
          </p>
          <CopyDetails id="areascore-input" title="Inputs for a scoped review" tone="light">
          <ul className="space-y-2 max-w-3xl">
            {inputs.map((i) => (
              <li key={i} className="flex items-start gap-3 py-3 border-b" style={{ borderColor: "var(--ols-hairline)" }}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C8102E" }} />
                <span className="text-[15px]" style={{ color: "var(--ols-navy)" }}>{i}</span>
              </li>
            ))}
          </ul>
          </CopyDetails>
        </div>
      </section>

      {/* WHAT ONWARD REVIEWS */}
      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="ols-eyebrow mb-3">What Onward Reviews</p>
          <h3 className="ols-h3 mb-6" style={{ color: "var(--ols-navy)" }}>Land, access and open questions.</h3>
          <CopyDetails id="areascore-review" title="Review criteria and evidence" tone="light">
          <ul className="grid md:grid-cols-2 gap-2">
            {reviewAreas.map((f) => (
              <li key={f} className="flex items-start gap-3 py-3 border-b" style={{ borderColor: "var(--ols-hairline)" }}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C8102E" }} />
                <span className="text-[15px]" style={{ color: "var(--ols-navy)" }}>{f}</span>
              </li>
            ))}
          </ul>
          </CopyDetails>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="ols-section" style={{ background: "var(--ols-limestone)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="ols-eyebrow mb-3">What Your Team Receives</p>
          <h2 className="ols-h2 mb-8">A usable area comparison.</h2>
          <p className="ols-lede" data-testid="areascore-deliverables-summary">A comparison map, findings and tradeoffs, agreed GIS files, and a prioritized due-diligence plan.</p>
          <CopyDetails id="areascore-deliverables" title="Detailed deliverables" tone="light">
          <div className="grid md:grid-cols-3 gap-3">
            {deliverables.map((d) => (
              <div key={d} className="flex items-start gap-3 py-4 border-t" style={{ borderColor: "var(--ols-hairline)" }}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C8102E" }} />
                <span className="text-[15px]" style={{ color: "var(--ols-navy)" }}>{d}</span>
              </div>
            ))}
          </div>
          </CopyDetails>
        </div>
      </section>

      {/* LIMITATIONS */}
      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="h-6 w-6" style={{ color: "#C8102E" }} />
            <p className="ols-eyebrow" style={{ color: "#C8102E" }}>Decision Support Built for Professional Judgment</p>
          </div>
          <h2 className="ols-h2 mb-6">Scope and professional judgment.</h2>
          <p className="text-[15px] leading-relaxed mb-6" style={{ color: "var(--ols-body)" }}>
            AreaScore is a professional service, not self-service software or a site approval. Appraisal-district values are context, not formal appraisals or purchase prices. Source gaps and specialist follow-up remain visible.
          </p>
          <CopyDetails id="areascore-boundaries" title="Review limitations" tone="light">
          <ul className="space-y-2">
            {notWhat.map((n) => (
              <li key={n} className="flex items-start gap-3 py-3 border-b" style={{ borderColor: "var(--ols-hairline)" }}>
                <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C8102E" }} />
                <span className="text-[15px]" style={{ color: "var(--ols-navy)" }}>{n}</span>
              </li>
            ))}
          </ul>
          </CopyDetails>
          <p className="text-[13px] italic mt-6" style={{ color: "var(--ols-body-muted)" }}>
            Licensed survey work is performed by properly licensed survey professionals. Onward coordinates the land, access, communication, and project workflow around that work.
          </p>
        </div>
      </section>

      {/* FINAL */}
      <section style={{ background: "linear-gradient(180deg, #090A0C 0%, #101720 100%)" }} className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="ols-h2 mb-6" style={{ color: "#fff" }}>Bring Us the Area Question.</h2>
          <p className="text-[15px] leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: "#DCE1EB" }}>
            Project discussions and scoped demonstrations are available. We will help define the review and next steps.
          </p>
          <div className="flex justify-center">
            <Link to="/contact?type=AreaScore&source=areascore_demo" className="ols-btn-primary" data-testid="areascore-final-cta">
              Talk to Onward About AreaScore <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AreaScore;
